// Mock coach data and preferences
import type { Coach, CoachPreferences, Specialization, Certification } from '@/types'

export const mockCoach: Coach = {
  id: 'coach-001',
  email: 'coach@fitcoach.com',
  name: 'Alex Thompson',
  profilePhoto: '/mock-avatars/coach-alex.jpg',
  specializations: [
    {
      id: 'spec-001',
      name: 'Strength Training',
      category: 'Performance',
      verified: true
    },
    {
      id: 'spec-002',
      name: 'Weight Loss',
      category: 'Body Composition',
      verified: true
    },
    {
      id: 'spec-003',
      name: 'Functional Movement',
      category: 'Rehabilitation',
      verified: true
    }
  ],
  certifications: [
    {
      id: 'cert-001',
      name: 'NASM Certified Personal Trainer',
      organization: 'National Academy of Sports Medicine',
      issueDate: new Date('2020-03-15'),
      expirationDate: new Date('2026-03-15'),
      credentialId: 'NASM-CPT-123456',
      verified: true
    },
    {
      id: 'cert-002',
      name: 'Precision Nutrition Level 1',
      organization: 'Precision Nutrition',
      issueDate: new Date('2021-06-10'),
      expirationDate: new Date('2027-06-10'),
      credentialId: 'PN1-789012',
      verified: true
    },
    {
      id: 'cert-003',
      name: 'Functional Movement Screen',
      organization: 'Functional Movement Systems',
      issueDate: new Date('2022-01-20'),
      expirationDate: new Date('2025-01-20'),
      credentialId: 'FMS-345678',
      verified: true
    }
  ],
  bio: 'Passionate fitness coach with 5+ years of experience helping clients achieve their health and fitness goals. Specializing in strength training, weight management, and functional movement patterns. I believe in creating sustainable lifestyle changes through progressive, science-based training methods.',
  phoneNumber: '+1-555-COACH1',
  timezone: 'America/New_York',
  status: 'active',
  createdAt: new Date('2020-03-01'),
  updatedAt: new Date('2024-01-25')
}

export const mockCoachPreferences: CoachPreferences = {
  theme: 'light',
  notifications: {
    email: true,
    push: true,
    sms: false,
    clientMessages: true,
    workoutCompletions: true,
    missedWorkouts: true,
    progressMilestones: true,
    scheduleChanges: true
  },
  workingHours: {
    monday: [
      { start: '06:00', end: '12:00' },
      { start: '15:00', end: '20:00' }
    ],
    tuesday: [
      { start: '06:00', end: '12:00' },
      { start: '15:00', end: '20:00' }
    ],
    wednesday: [
      { start: '06:00', end: '12:00' },
      { start: '15:00', end: '20:00' }
    ],
    thursday: [
      { start: '06:00', end: '12:00' },
      { start: '15:00', end: '20:00' }
    ],
    friday: [
      { start: '06:00', end: '12:00' },
      { start: '15:00', end: '19:00' }
    ],
    saturday: [
      { start: '08:00', end: '14:00' }
    ],
    sunday: [] // Rest day
  },
  autoResponses: true,
  clientCapacity: 25,
  defaultProgramDuration: 12 // weeks
}

// Available specializations for coaches
export const availableSpecializations: Specialization[] = [
  { id: 'spec-strength', name: 'Strength Training', category: 'Performance', verified: false },
  { id: 'spec-cardio', name: 'Cardiovascular Training', category: 'Performance', verified: false },
  { id: 'spec-weight-loss', name: 'Weight Loss', category: 'Body Composition', verified: false },
  { id: 'spec-muscle-gain', name: 'Muscle Building', category: 'Body Composition', verified: false },
  { id: 'spec-functional', name: 'Functional Movement', category: 'Rehabilitation', verified: false },
  { id: 'spec-injury', name: 'Injury Prevention', category: 'Rehabilitation', verified: false },
  { id: 'spec-sports', name: 'Sport-Specific Training', category: 'Performance', verified: false },
  { id: 'spec-senior', name: 'Senior Fitness', category: 'Special Populations', verified: false },
  { id: 'spec-youth', name: 'Youth Training', category: 'Special Populations', verified: false },
  { id: 'spec-prenatal', name: 'Prenatal/Postnatal', category: 'Special Populations', verified: false },
  { id: 'spec-nutrition', name: 'Nutrition Coaching', category: 'Lifestyle', verified: false },
  { id: 'spec-behavior', name: 'Behavior Change', category: 'Lifestyle', verified: false }
]

// Available certifications
export const availableCertifications = [
  'NASM Certified Personal Trainer',
  'ACE Personal Trainer',
  'ACSM Certified Personal Trainer',
  'NSCA Certified Strength and Conditioning Specialist',
  'Precision Nutrition Level 1',
  'Precision Nutrition Level 2',
  'Functional Movement Screen',
  'SFMA Certified',
  'Corrective Exercise Specialist',
  'Performance Enhancement Specialist',
  'Youth Exercise Specialist',
  'Senior Exercise Specialist',
  'Prenatal Exercise Specialist',
  'Sports Nutrition Specialist'
]

// Coach performance metrics
export interface CoachPerformanceMetrics {
  totalClients: number
  activeClients: number
  clientRetentionRate: number
  averageClientRating: number
  averageResponseTime: number // hours
  weeklyActiveSessions: number
  monthlyNewClients: number
  clientSuccessRate: number // % achieving primary goals
}

export const mockCoachMetrics: CoachPerformanceMetrics = {
  totalClients: 24,
  activeClients: 21,
  clientRetentionRate: 87.5,
  averageClientRating: 4.7,
  averageResponseTime: 2.3,
  weeklyActiveSessions: 18,
  monthlyNewClients: 3,
  clientSuccessRate: 82
}

// Coach business insights
export interface CoachBusinessInsights {
  monthlyRevenue?: number
  averageSessionRate?: number
  popularPrograms: string[]
  peakHours: string[]
  clientDemographics: {
    ageGroups: { range: string; percentage: number }[]
    goals: { goal: string; percentage: number }[]
    experience: { level: string; percentage: number }[]
  }
}

export const mockBusinessInsights: CoachBusinessInsights = {
  monthlyRevenue: 4500,
  averageSessionRate: 75,
  popularPrograms: [
    'Beginner Strength Program',
    'Weight Loss Circuit Training',
    'Upper/Lower Split'
  ],
  peakHours: ['07:00-09:00', '17:00-19:00'],
  clientDemographics: {
    ageGroups: [
      { range: '18-25', percentage: 15 },
      { range: '26-35', percentage: 40 },
      { range: '36-45', percentage: 30 },
      { range: '46-55', percentage: 15 }
    ],
    goals: [
      { goal: 'Weight Loss', percentage: 45 },
      { goal: 'Strength Building', percentage: 30 },
      { goal: 'General Fitness', percentage: 15 },
      { goal: 'Sport Performance', percentage: 10 }
    ],
    experience: [
      { level: 'Beginner', percentage: 40 },
      { level: 'Intermediate', percentage: 45 },
      { level: 'Advanced', percentage: 15 }
    ]
  }
}

// Helper functions
export const getCoachWorkingHours = (day: string): { start: string; end: string }[] => {
  const dayKey = day.toLowerCase() as keyof typeof mockCoachPreferences.workingHours
  return mockCoachPreferences.workingHours[dayKey] || []
}

export const isCoachAvailable = (day: string, time: string): boolean => {
  const workingHours = getCoachWorkingHours(day)
  return workingHours.some(slot => 
    time >= slot.start && time <= slot.end
  )
}

export const getCoachSpecializationsByCategory = (category: string): Specialization[] => {
  return mockCoach.specializations.filter(spec => spec.category === category)
}

export const getActiveCertifications = (): Certification[] => {
  const now = new Date()
  return mockCoach.certifications.filter(cert => 
    !cert.expirationDate || cert.expirationDate > now
  )
}